import { useState, useMemo, useEffect } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Comment } from 'infrastructure/classes/comments/comment';
import { ModuleType } from 'infrastructure/enums';
import { STALE_TIME } from 'infrastructure/consts/stale-time';
import { BroadcastTopics } from 'services/wsService';

import useApi from '../use-api';
import {
  addDocumentationTime,
  generalCommentsApi,
  generateCommentsQueryKey,
} from './common';
import { useSocketOn } from '../websockets/use-socket-on';

import type { InfiniteData } from '@tanstack/react-query';
import type { IUseFetchCommentsProps, IPaginatedCommentsData } from './types';
import type {
  ICommentPaginatedData,
  IUseCommentMeta,
} from 'infrastructure/interfaces';
import type { ISocketInteractiveTimeData } from 'infrastructure/interfaces/i-rpm-patient-activity';

export const useFetchComments = ({
  patientGuid,
  meta: metaProps,
  enabled = true,
}: IUseFetchCommentsProps) => {
  const [meta, setMeta] = useState<IUseCommentMeta>({
    page: 1,
    items: 50,
    ...metaProps,
  });
  const queryClient = useQueryClient();

  const { loadData } = useApi({ canAbort: false });

  const queryKey = useMemo(
    () => generateCommentsQueryKey({ patientGuid, ...meta }),
    [patientGuid, meta.commentType, meta.startTime, meta.endTime],
  );

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery<IPaginatedCommentsData>({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const res = await loadData<ICommentPaginatedData>(
        generalCommentsApi(patientGuid),
        {
          page: pageParam,
          items: meta.items,
          commentType: meta.commentType,
          startTime: meta.startTime,
          endTime: meta.endTime,
        },
      );

      if (!res || !res.data || res.data.length === 0) {
        return {
          data: [],
          nextPage: undefined,
        };
      }

      return {
        data: res.data.map((el: any) => new Comment(el)),
        nextPage:
          res.data.length < (meta.items ?? 50) ? undefined : pageParam + 1,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: STALE_TIME,
    enabled:
      enabled &&
      Boolean(patientGuid) &&
      Boolean(meta.commentType) &&
      meta.commentType !== ModuleType.ADMIN,
  });

  useEffect(() => {
    if (metaProps) {
      setMeta((prev) => ({
        ...prev,
        ...metaProps,
      }));
    }
  }, [
    setMeta,
    metaProps?.commentType,
    metaProps?.endTime,
    metaProps?.startTime,
    metaProps?.items,
    metaProps?.page,
    metaProps?.reverse,
  ]);

  useSocketOn<ISocketInteractiveTimeData>(
    BroadcastTopics.InteractiveTimeInsert,
    (socketData) => {
      queryClient.setQueryData<InfiniteData<IPaginatedCommentsData>>(
        queryKey,
        (oldData) => addDocumentationTime(oldData, socketData),
      );
    },
  );

  return {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    meta,
    setMeta,
    refetch,
    queryKey,
  };
};
